import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUser } from '@clerk/clerk-react';
import { getSupabase } from '@/lib/supabase';
import { useAuthReady } from '@/hooks/useAuthReady';
import { checkCanAddProperty } from '@/lib/stripe';
import { subscriptionKeys } from './useSubscription';
import type { Property, CreatePropertyInput } from '@/types/database';

// Query keys
export const propertyKeys = {
  all: ['properties'] as const,
  lists: () => [...propertyKeys.all, 'list'] as const,
  list: (landlordId: string) => [...propertyKeys.lists(), landlordId] as const,
  paginated: (landlordId: string, page: number, pageSize: number) =>
    [...propertyKeys.lists(), landlordId, 'page', page, pageSize] as const,
  details: () => [...propertyKeys.all, 'detail'] as const,
  detail: (id: string) => [...propertyKeys.details(), id] as const,
};

// Fetch all properties for current user
export function useProperties() {
  const { userId, isReady } = useAuthReady();

  return useQuery({
    queryKey: propertyKeys.list(userId ?? ''),
    queryFn: async (): Promise<Property[]> => {
      if (!userId) return [];

      const { data, error } = await getSupabase()
        .from('properties')
        .select('*')
        .eq('landlord_id', userId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data ?? [];
    },
    enabled: isReady,
  });
}

// Fetch properties with server-side pagination
export function usePaginatedProperties(page: number = 1, pageSize: number = 20) {
  const { userId, isReady } = useAuthReady();

  return useQuery({
    queryKey: propertyKeys.paginated(userId ?? '', page, pageSize),
    queryFn: async () => {
      if (!userId) return { data: [] as Property[], count: 0 };

      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      const { data, error, count } = await getSupabase()
        .from('properties')
        .select('*', { count: 'exact' })
        .eq('landlord_id', userId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;
      return { data: (data ?? []) as Property[], count: count ?? 0 };
    },
    enabled: isReady,
    placeholderData: (prev) => prev,
  });
}

// Fetch single property by ID
export function useProperty(id: string | undefined) {
  const { userId, isReady } = useAuthReady();

  return useQuery({
    queryKey: propertyKeys.detail(id ?? ''),
    queryFn: async (): Promise<Property | null> => {
      if (!id || !userId) return null;

      const { data, error } = await getSupabase()
        .from('properties')
        .select('*')
        .eq('id', id)
        .eq('landlord_id', userId)
        .is('deleted_at', null)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null; // Not found
        throw error;
      }
      return data;
    },
    enabled: !!id && isReady,
  });
}

// Create property mutation
export function useCreateProperty() {
  const queryClient = useQueryClient();
  const { user } = useUser();

  return useMutation({
    mutationFn: async (input: CreatePropertyInput): Promise<Property> => {
      if (!user?.id) throw new Error('Not authenticated');

      // Tier-based property limit (matches plan's max units)
      const limitInfo = await checkCanAddProperty(user.id);
      // A failed check is "unknown", not "0 allowed" — the trigger enforces it.
      if (!limitInfo.allowed && limitInfo.subscriptionStatus !== 'error') {
        throw new Error(
          `PROPERTY_LIMIT_REACHED:${limitInfo.currentProperties}:${limitInfo.maxProperties}`
        );
      }

      const { data, error } = await getSupabase()
        .from('properties')
        .insert({
          ...input,
          landlord_id: user.id,
          images: [],
          is_active: true,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: propertyKeys.lists() });
      if (user?.id) {
        queryClient.invalidateQueries({
          queryKey: subscriptionKeys.canAddProperty(user.id),
        });
        queryClient.invalidateQueries({
          queryKey: subscriptionKeys.billing(user.id),
        });
      }
    },
  });
}

// Update property mutation
export function useUpdateProperty() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: Partial<Property> & { id: string }): Promise<Property> => {
      const { data, error } = await getSupabase()
        .from('properties')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: propertyKeys.lists() });
      queryClient.invalidateQueries({ queryKey: propertyKeys.detail(data.id) });
    },
  });
}

// Soft delete property mutation (moves to trash for 30 days)
// Blocks if property has active bookings.
export interface DeletePropertyResult {
  ok: boolean;
  error_code?: 'HAS_ACTIVE_BOOKINGS' | 'FORBIDDEN' | 'NOT_FOUND';
  error_message?: string;
  cascade_units?: number;
}

export function useDeleteProperty() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<DeletePropertyResult> => {
      const { data, error } = await getSupabase().rpc('soft_delete_property', {
        p_property_id: id,
      });
      if (error) throw error;

      const result = data as DeletePropertyResult;
      if (!result.ok) {
        const err = new Error(result.error_message || result.error_code || 'Delete failed') as Error & {
          code?: string;
          result?: DeletePropertyResult;
        };
        err.code = result.error_code;
        err.result = result;
        throw err;
      }
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: propertyKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['units'] });
      queryClient.invalidateQueries({ queryKey: ['trash'] });
      // The calendars and the occupancy card list units too.
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      queryClient.invalidateQueries({ queryKey: ['seasonalOccupancy'] });
      // Trashed properties/units no longer count against the plan.
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
    },
  });
}

// Toggle property active status
export function useTogglePropertyStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      isActive,
    }: {
      id: string;
      isActive: boolean;
    }): Promise<Property> => {
      const { data, error } = await getSupabase()
        .from('properties')
        .update({
          is_active: isActive,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: propertyKeys.lists() });
      queryClient.invalidateQueries({ queryKey: propertyKeys.detail(data.id) });
    },
  });
}
